/**
 * Usage Dashboard Service
 *
 * Provides API calls for fetching usage dashboard metrics.
 * Data is scoped by billing account with optional workspace filtering.
 *
 * TIMEZONE HANDLING:
 * All date range queries include the user's timezone so the backend can
 * properly convert dates to UTC. This ensures that when a user in PST
 * selects "Jan 26", they get data from their full day (not UTC midnight).
 */

import type {
  CreditsData,
  DashboardData,
  SavingsData,
  UsageOverviewData
} from '~/components/usage-dashboard/types'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { getTimezoneName } from '~/utils/timezone'

// ============================================================================
// Types
// ============================================================================

export type TimeRange = '7d' | '14d' | '30d' | '90d' | 'billing_cycle' | 'custom'

export interface DashboardParams {
  timeRange: TimeRange
  page?: number
  pageSize?: number
  workspaceUuid?: string // Optional workspace filter (null = all workspaces)
  startDate?: string // Date string in YYYY-MM-DD format for custom range
  endDate?: string // Date string in YYYY-MM-DD format for custom range
  timezone?: string // IANA timezone name (auto-detected if not provided)
}

export interface PersonalUsageParams {
  timeRange: TimeRange
  workspaceUuid?: string // Optional workspace filter (undefined = all workspaces)
  startDate?: string // Date string in YYYY-MM-DD format for custom range
  endDate?: string // Date string in YYYY-MM-DD format for custom range
  timezone?: string // IANA timezone name (auto-detected if not provided)
}

export interface RecentActivity {
  id: string
  title: string
  time: string
  messages: number
  type: 'conversation' | 'task'
}

export interface PersonalUsageData {
  name: string
  last_active: string
  usage_overview: UsageOverviewData
  savings: SavingsData
  credits: CreditsData
  recent_activities: RecentActivity[]
}

export interface BillableFlowDetail {
  flow_name: string
  flow_type: string
  repo_name: string
  workspace_name: string
  date: string | null
  task_ids?: number[]
  kept_task_id?: number | null
  credits_used: number
}

export interface BillableFlowDetailsResponse {
  flows: BillableFlowDetail[]
  total?: number
  total_tasks?: number
  total_credits?: number
  page?: number
  page_size?: number
}

export interface BillableFlowDetailsParams {
  timeRange: TimeRange
  workspaceUuid?: string
  startDate?: string
  endDate?: string
  timezone?: string
  personalOnly?: boolean
  page?: number
  pageSize?: number
}

export interface BillableFlowTask {
  task_id: number
  conversation_id: string | null
  created_time: string | null
}

export interface BillableFlowEntry {
  name: string
  category: string
  task_count: number
  tasks: BillableFlowTask[]
}

export interface BillableFlowDetails {
  raw_count: number
  unique_count: number
  duplicate_count: number
  flows: BillableFlowEntry[]
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get dashboard metrics for a billing account (with optional workspace filter)
 *
 * For custom date ranges, sends timezone so backend can properly convert
 * dates to UTC. Timezone is auto-detected from browser if not provided.
 */
export async function getDashboardMetrics(
  billingAccountId: number,
  params: DashboardParams
): Promise<DashboardData> {
  // Auto-detect timezone if not provided and we have custom date range
  const timezone = params.timezone ?? getTimezoneName()

  const queryParams = new URLSearchParams({
    time_range: params.timeRange,
    timezone, // Always send timezone for proper date conversion
    ...(params.page && { page: String(params.page) }),
    ...(params.pageSize && { page_size: String(params.pageSize) }),
    ...(params.workspaceUuid && { workspace_uuid: params.workspaceUuid }),
    ...(params.startDate && { start_date: params.startDate }),
    ...(params.endDate && { end_date: params.endDate })
  })

  const url = `/api/billing/${billingAccountId}/usage/dashboard?${queryParams}`
  return fetchWrapper.get<DashboardData>(url)
}

/**
 * Get personal usage metrics for the current user within a billing account
 *
 * For custom date ranges, sends timezone so backend can properly convert
 * dates to UTC. Timezone is auto-detected from browser if not provided.
 */
export async function getPersonalUsage(
  billingAccountId: number,
  params: PersonalUsageParams
): Promise<PersonalUsageData> {
  // Auto-detect timezone if not provided and we have custom date range
  const timezone = params.timezone ?? getTimezoneName()

  const queryParams = new URLSearchParams({
    time_range: params.timeRange,
    timezone, // Always send timezone for proper date conversion
    ...(params.workspaceUuid && { workspace_uuid: params.workspaceUuid }),
    ...(params.startDate && { start_date: params.startDate }),
    ...(params.endDate && { end_date: params.endDate })
  })

  const url = `/api/billing/${billingAccountId}/usage/personal?${queryParams}`
  return fetchWrapper.get<PersonalUsageData>(url)
}

/**
 * Get detailed billable flow entries for a billing account.
 *
 * Returns individual flow records with name, type, date, and associated task.
 * Set personalOnly=true to restrict to the current user's flows.
 */
export async function getBillableFlowDetails(
  billingAccountId: number,
  params: BillableFlowDetailsParams
): Promise<BillableFlowDetailsResponse> {
  const timezone = params.timezone ?? getTimezoneName()

  const queryParams = new URLSearchParams({
    time_range: params.timeRange,
    timezone,
    ...(params.workspaceUuid && { workspace_uuid: params.workspaceUuid }),
    ...(params.startDate && { start_date: params.startDate }),
    ...(params.endDate && { end_date: params.endDate }),
    ...(params.personalOnly != null && { personal_only: String(params.personalOnly) }),
    ...(params.page != null && { page: String(params.page) }),
    ...(params.pageSize != null && { page_size: String(params.pageSize) })
  })

  const url = `/api/billing/${billingAccountId}/usage/billable-flow-details?${queryParams}`
  const raw = await fetchWrapper.get<BillableFlowDetailsResponse & { Total?: number }>(url)
  // Some gateways have surfaced PascalCase; normalize so pagination can trust `total` when present.
  const total =
    typeof raw.total === 'number'
      ? raw.total
      : typeof raw.Total === 'number'
        ? raw.Total
        : undefined
  return { ...raw, total }
}
